import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/HrDashboardFilters.css";
import { fetchCompanyCodesById } from "../Api/InvoiceApi";
import { Base_URL } from "../url";

const HrDashboardFilters = ({
  onFilterChange = () => {},
  initialFilters = {},
  disabled = false
}) => {
  const { user } = useAuth();
  const userId = user?.userId;

  const [orgIds, setOrgIds] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState({
    orgs: false,
    companies: false,
    fiscalYears: false
  });
  const [error, setError] = useState("");
  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState({
    orgId: "",
    companyCode: "",
    fiscalYear: currentYear
  });

  /* ===================== FETCH COMPANY CODES ===================== */
  const fetchCompanyCodes = useCallback(async (orgId) => {
    if (!orgId) return;

    try {
      setLoading(prev => ({ ...prev, companies: true }));
      const res = await fetchCompanyCodesById(orgId);
      const data = res?.data || res;

      const companies = Array.isArray(data)
        ? data.map(c => ({
            code: c.companyCode,
            name: c.name?.replace(/\r?\n/g, " ")
          }))
        : [];

      setCompanyCodes(companies);

          if (companies.length > 0) {
      setFilters(prev => {
        const updated = {
          ...prev,
          companyCode: companies[0].code
        };
        onFilterChange?.(updated);
        return updated;
      });
    }

    } catch (err) {
      console.error(err);
      setCompanyCodes([]);
    } finally {
      setLoading(prev => ({ ...prev, companies: false }));
    }
  }, []);

  /* ===================== FETCH ORGANISATION ===================== */
  const fetchOrgIds = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(prev => ({ ...prev, orgs: true }));
      setError("");

      const res = await fetch(
        `${Base_URL}/Resource/GetOrganisationByUser/${userId}`
      );
      const data = await res.json();

      if (data?.organisationId) {
        const orgList = [
          {
            id: String(data.organisationId),
            name: data.organisationName
          }
        ];

        setOrgIds(orgList);

        const initialOrgId = String(data.organisationId);
        setFilters(prev => ({
          ...prev,
          orgId: initialOrgId
        }));

        fetchCompanyCodes(initialOrgId);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load organization");
    } finally {
      setLoading(prev => ({ ...prev, orgs: false }));
    }
  }, [userId, fetchCompanyCodes]);

  /* ===================== FETCH FISCAL YEARS ===================== */
  const fetchFiscalYears = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, fiscalYears: true }));
      const res = await fetch(`${Base_URL}/HrDashboard/financialyears`);
      const data = await res.json();
      setFiscalYears(Array.isArray(data) ? data : []);
    } finally {
      setLoading(prev => ({ ...prev, fiscalYears: false }));
    }
  }, []);

  /* ===================== HANDLERS ===================== */
  const handleOrgChange = (e) => {
    const orgId = e.target.value;
    const newFilters = {
      orgId,
      companyCode: "",
      fiscalYear: currentYear
    };

    setFilters(newFilters);
    setCompanyCodes([]);
    fetchCompanyCodes(orgId);
    onFilterChange?.(newFilters);
  };

  const handleCompanyChange = (e) => {
    const companyCode = e.target.value;
    const newFilters = { ...filters, companyCode };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const handleFiscalYearChange = (e) => {
  const value = e.target.value;
  const fiscalYear = value ? Number(value) : currentYear;

  const newFilters = { ...filters, fiscalYear };
  setFilters(newFilters);
  onFilterChange?.(newFilters);
};

  const handleResetFilters = () => {
    const orgId = orgIds[0]?.id || "";
    const reset = {
      orgId,
      companyCode: "",
      fiscalYear: currentYear
    };

    setFilters(reset);
    setCompanyCodes([]);
    if (orgId) fetchCompanyCodes(orgId);
    onFilterChange?.(reset);
  };

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    fetchOrgIds();
    fetchFiscalYears();
  }, [fetchOrgIds, fetchFiscalYears]);

  /* ===================== UI ===================== */
  return (
    <div className="hr-dashboard-filters">
      <div className="filter-grid">

        <div className="filter-group">
          <label>Organization</label>
          <select
            value={filters.orgId}
            onChange={handleOrgChange}
            disabled={loading.orgs || disabled}
          >
            <option value="">-- Select Organization --</option>
            {orgIds.map(o => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Company</label>
          <select
            value={filters.companyCode}
            onChange={handleCompanyChange}
            disabled={!filters.orgId || loading.companies}
          >
            <option value="">Select Company </option>
            {companyCodes.map(c => (
              <option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Fiscal Year</label>
          <select value={filters.fiscalYear} onChange={handleFiscalYearChange}>
  <option value="">Select Fiscal Year</option>
  {fiscalYears.map(y => (
    <option key={y} value={y}>{y}</option>
  ))}
</select>

        </div>

        <div className="filter-group filter-actions">
          <button onClick={() => onFilterChange(filters)}>Apply</button>
          <button onClick={handleResetFilters}>Reset</button>
        </div>

      </div>

      {error && <div className="filter-error">{error}</div>}
    </div>
  );
};

export default HrDashboardFilters;
